import { Link } from 'react-router-dom'
import {
  FileText, BarChart3, Folder, ListChecks, Plus, Eye, CheckCircle2,
  XCircle, Clock, Image as ImageIcon, Video, Music, RefreshCw,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { contentApi, type ContentStatsOverview, type ContentListItem, type ContentCategory } from '../../lib/api'

const ContentDashboard = () => {
  const [stats, setStats] = useState<ContentStatsOverview | null>(null)
  const [recent, setRecent] = useState<ContentListItem[]>([])
  const [drafts, setDrafts] = useState<ContentListItem[]>([])
  const [cats, setCats] = useState<ContentCategory[]>([])
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const [s, recentRes, draftRes, c] = await Promise.all([
        contentApi.adminStats(),
        contentApi.adminListContents({ page: 1, page_size: 5, status: 'published' }).catch(() => ({ items: [], total: 0 })),
        contentApi.adminListContents({ page: 1, page_size: 5, status: 'draft' }).catch(() => ({ items: [], total: 0 })),
        contentApi.adminListCategoriesAll().catch(() => []),
      ])
      setStats(s)
      setRecent(recentRes.items || [])
      setDrafts(draftRes.items || [])
      setCats(c || [])
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const mediaColor: Record<string, string> = { text: 'bg-blue-100 text-blue-700', video: 'bg-purple-100 text-purple-700', audio: 'bg-orange-100 text-orange-700' }
  const mediaName: Record<string, string> = { text: '图文', video: '视频', audio: '音频' }

  const activeCats = cats.filter(c => c.is_active === 1).length

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">内容运营工作台</h1>
          <p className="text-sm text-gray-500 mt-1">管理科普文章、慢病专题、实用工具和区县公告。</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded hover:bg-gray-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
          </button>
          <Link to="/admin/content/new" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700">
            <Plus className="w-4 h-4" /> 新建内容
          </Link>
        </div>
      </div>

      {/* 核心指标（总数含全部状态；媒体分布仅统计已发布） */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card title="内容总数" value={stats?.total} sub={`今日新增 ${stats?.today_new ?? 0} · 启用分类 ${activeCats}`} Icon={FileText} color="bg-blue-50 text-blue-600" />
        <Link to="/admin/content/review-queue" className="bg-white border rounded p-4 hover:border-red-300 transition-colors">
          <div className="flex items-center gap-3">
            <div className="rounded p-2 bg-red-50 text-red-600"><Clock className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-2xl font-semibold">{stats?.pending_review ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">待审核</div>
            </div>
          </div>
          {(stats?.pending_review ?? 0) > 0 && <div className="text-xs text-red-500 mt-2 truncate">有待处理内容，点击处理 →</div>}
        </Link>
        <Card title="已发布" value={stats?.published} Icon={CheckCircle2} color="bg-green-50 text-green-600" />
        <Card title="已下线" value={stats?.offline} Icon={XCircle} color="bg-yellow-50 text-yellow-600" />
        <Card title="草稿待办" value={stats?.draft} sub={drafts[0] ? `最近：${drafts[0].title.slice(0, 12)}…` : '暂无'} Icon={Clock} color="bg-gray-50 text-gray-600" />
        <Card title={'近 ' + (stats?.days || 7) + ' 日阅读'} value={stats?.recent_views} Icon={Eye} color="bg-purple-50 text-purple-600" />
      </div>

      {/* 媒体分布 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(['text', 'video', 'audio'] as const).map(mt => (
          <div key={mt} className="bg-white border rounded p-4 flex items-center gap-3">
            <div className={`rounded p-2 ${mediaColor[mt]}`}>
              {mt === 'text' ? <FileText className="w-5 h-5" /> : mt === 'video' ? <Video className="w-5 h-5" /> : <Music className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-2xl font-semibold">{stats?.by_media_type?.[mt] || 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">{mediaName[mt]}内容（已发布）</div>
            </div>
          </div>
        ))}
      </div>

      {/* 快捷入口 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/admin/content" className="bg-white border rounded p-4 hover:bg-gray-50">
          <h3 className="font-medium flex items-center gap-1"><FileText className="w-4 h-4" />内容列表</h3>
          <p className="text-xs text-gray-500 mt-1">查看 / 编辑 / 发布 / 下线</p>
        </Link>
        <Link to="/admin/content/categories" className="bg-white border rounded p-4 hover:bg-gray-50">
          <h3 className="font-medium flex items-center gap-1"><Folder className="w-4 h-4 text-yellow-500" />分类管理</h3>
          <p className="text-xs text-gray-500 mt-1">维护两级分类树</p>
        </Link>
        <Link to="/admin/content/statistics" className="bg-white border rounded p-4 hover:bg-gray-50">
          <h3 className="font-medium flex items-center gap-1"><BarChart3 className="w-4 h-4" />内容统计</h3>
          <p className="text-xs text-gray-500 mt-1">查看分布与阅读趋势</p>
        </Link>
        <Link to="/admin/content/new" className="bg-white border rounded p-4 hover:bg-gray-50">
          <h3 className="font-medium flex items-center gap-1"><Plus className="w-4 h-4" />新建内容</h3>
          <p className="text-xs text-gray-500 mt-1">图文 / 视频 / 音频</p>
        </Link>
      </div>

      {/* 最近发布 + 待处理草稿 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded">
          <div className="p-3 border-b flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" />最近发布</h2>
            <Link to="/admin/content?status=published" className="text-xs text-gray-500 hover:text-gray-700">查看全部</Link>
          </div>
          <ul className="divide-y">
            {recent.length === 0 && <li className="p-4 text-xs text-gray-400 text-center">暂无已发布内容</li>}
            {recent.map(c => (
              <li key={c.id} className="p-3 hover:bg-gray-50">
                <Link to={`/admin/content/${c.id}`} className="flex items-center gap-2">
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded ${mediaColor[c.media_type] || mediaColor.text}`}>{mediaName[c.media_type]}</span>
                  <span className="text-sm truncate flex-1">{c.title}</span>
                  <span className="text-xs text-gray-400">{c.view_count} 阅读</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border rounded">
          <div className="p-3 border-b flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-1"><Clock className="w-4 h-4 text-gray-500" />待处理草稿</h2>
            <Link to="/admin/content?status=draft" className="text-xs text-gray-500 hover:text-gray-700">查看全部</Link>
          </div>
          <ul className="divide-y">
            {drafts.length === 0 && <li className="p-4 text-xs text-gray-400 text-center">无草稿待办</li>}
            {drafts.map(c => (
              <li key={c.id} className="p-3 hover:bg-gray-50">
                <Link to={`/admin/content/${c.id}`} className="flex items-center gap-2">
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded ${mediaColor[c.media_type] || mediaColor.text}`}>{mediaName[c.media_type]}</span>
                  <span className="text-sm truncate flex-1">{c.title}</span>
                  <span className="text-xs text-gray-400">{(c as any).updated_at?.slice?.(0, 10) || '-'}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

const Card = ({ title, value, sub, Icon, color }: any) => (
  <div className="bg-white border rounded p-4">
    <div className="flex items-center gap-3">
      <div className={`rounded p-2 ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-2xl font-semibold">{value ?? '-'}</div>
        <div className="text-xs text-gray-500 mt-0.5">{title}</div>
      </div>
    </div>
    {sub && <div className="text-xs text-gray-400 mt-2 truncate">{sub}</div>}
  </div>
)

export default ContentDashboard